import { defineStore } from 'pinia';
import { awaitTask } from '@frontend/ui/features/checkup/helpers/tasks';

export const useFormDataStore = defineStore('formData', {
  state: () => ({
    // Form data
    forms: {},
    currentFormId: null,
    currentForm: null,

    // Entity data
    entities: {},
    entityFields: {}, // Maps entity IDs to their fields

    // Field mappings
    fieldMappings: {}, // Maps form field IDs to entity field IDs
    fieldValues: {}, // Current values for all fields
    fieldMetadata: {}, // Stores source fields, transformations, and confidence for fields

    // Processing state
    processingSections: [], // Array of section IDs currently being processed
    sectionEntityMapping: {}, // Map of section IDs to entity IDs
    highlightedSection: null,
    useDossier: false,
    useDocuments: false,
  }),

  getters: {
    currentForm: state => state.forms[state.currentFormId],

    // Add mappedFields getter
    mappedFields: state => {
      const form = state.forms[state.currentFormId];
      if (!form?.form_fields) return [];

      return form.form_fields.map(field => ({
        ...field,
        value: state.fieldValues[field.id] || field.value || '',
        mappedEntityField: state.fieldMappings[field.id],
      }));
    },

    // Add filteredEntities getter
    filteredEntities: state => {
      const entities = Object.values(state.entities);
      return entities.filter(
          entity => {
            if (state.useDossier && entity.type === 'dossier') {
              return true;
            }
            if (state.useDocuments && entity.type !== 'dossier') {
              return true;
            }
            return false;
          },
      );
    },

    formSections: state => {
      const form = state.forms[state.currentFormId];
      if (!form?.form_fields) return [];

      // Extract unique sections from form fields
      const sections = new Set();
      form.form_fields.forEach(field => {
        const section = field?.context?.section || field.meta?.section;
        if (section) {
          sections.add(section);
        }
      });

      // Convert to array of section objects
      return Array.from(sections).map(section => ({
        id: section,
        name: section,
      }));
    },

    sectionFields: state => sectionId => {
      const form = state.forms[state.currentFormId];
      if (!form) return [];
      return form.form_fields?.filter(field => field?.context?.section === sectionId || field?.meta?.section === sectionId) || [];
    },

    entitySections: state => entityId => {
      return Object.entries(state.sectionEntityMapping)
          .filter(([_, mappedEntityId]) => mappedEntityId === entityId)
          .map(([sectionId]) => sectionId);
    },

    getEntityFields: state => entityId => {
      return state.entityFields[entityId] || [];
    },

    sectionEntity: state => sectionId => {
      return state.sectionEntityMapping[sectionId];
    },

    // New computed property for annotations
    annotations: state => {
      const form = state.forms[state.currentFormId];
      if (!form?.form_fields) return [];

      return form.form_fields.map(field => ({
        ...field,
        value: state.fieldValues[field.id] || field.value || '',
        rect: field.rect || null,
        page: field.page || 1,
        type: field.type || 'text',
        meta: {
          ...field.meta,
          section: field?.context?.section || field.meta?.section || null,
          pageNumber: field.page || field.meta?.pageNumber || 1,
          rect: field.rect || field.meta?.rect || null,
          xPosition: field.meta?.xPosition || null,
          yPosition: field.meta?.yPosition || null,
          width: field.meta?.width || null,
          height: field.meta?.height || null,
        },
      }));
    },

    // Computed property for fields by page
    fieldsByPage: state => {
      const annotations = state.annotations;
      return annotations.reduce((acc, field) => {
        const page = field.page || 1;
        if (!acc[page]) {
          acc[page] = [];
        }
        acc[page].push(field);
        return acc;
      }, {});
    },
  },

  actions: {
    setCurrentForm(formId) {
      this.currentFormId = formId;
    },

    setFormData({ formId, formData }) {
      this.forms[formId] = formData;
    },

    setEntities(entities) {
      this.entities = entities;
    },

    setEntityFields({ entityId, fields }) {
      this.entityFields[entityId] = fields;
    },

    mapSectionToEntity({ sectionId, entityId }) {
      this.sectionEntityMapping[sectionId] = entityId;
    },

    mapField({ formFieldId, entityFieldId }) {
      this.fieldMappings[formFieldId] = entityFieldId;
    },

    setFieldValue({ fieldId, value, sourceFields, transformation, confidence }) {
      console.log('Setting field value:', { fieldId, value, sourceFields, transformation, confidence });

      // Update the value in the store
      this.fieldValues[fieldId] = value;

      // Update metadata if provided
      if (sourceFields || transformation || confidence !== undefined) {
        this.fieldMetadata[fieldId] = {
          ...(this.fieldMetadata[fieldId] || {}),
          sourceFields: sourceFields || [],
          transformation: transformation || '',
          confidence: confidence || 0,
        };
      }

      // Update the value in the current form if it exists
      if (this.currentForm?.form_fields) {
        const formField = this.currentForm.form_fields.find(f => f.id === fieldId);
        if (formField) {
          formField.value = value;
          if (sourceFields) formField.sourceFields = sourceFields;
          if (transformation) formField.transformation = transformation;
          if (confidence !== undefined) formField.confidence = confidence;
        }
      }

      // Emit a custom event to notify components of the change
      window.dispatchEvent(new CustomEvent('fieldValueChanged', {
        detail: { fieldId, value, sourceFields, transformation, confidence },
      }));
    },

    setProcessingSection({ sectionId, isProcessing }) {
      // Use Vue's reactivity by directly setting array
      if (isProcessing) {
        // Only add if not already processing
        if (!this.processingSections.includes(sectionId)) {
          this.processingSections.push(sectionId);
        }
      } else {
        this.processingSections = this.processingSections.filter(id => id !== sectionId);
      }
    },

    resetProcessingSections() {
      // Clear all processing sections
      this.processingSections = [];
    },

    setHighlightedSection(sectionId) {
      this.highlightedSection = sectionId;
    },

    clearFormData() {
      this.forms = {};
      this.currentFormId = null;
      this.sectionEntityMapping = {};
      this.fieldMappings = {};
      this.fieldValues = {};
      this.processingSections = [];
      this.highlightedSection = null;
    },

    async loadFormData(formId) {
      try {
        const response = await fetch(`/api/v1/pdf-forms/data/${formId}/`);
        if (!response.ok) throw new Error('Failed to fetch form data');

        const formData = await response.json();
        console.log('Raw form data:', formData);

        // Ensure fields are properly structured
        if (formData.form_fields) {
          formData.form_fields = formData.form_fields.map(field => ({
            ...field, // Preserve all original field data
            id: field.id || field.name,
            value: field.value || '',
            rect: field.rect || null,
            page: field.page || 1,
            type: field.type || 'text',
            meta: {
              ...field.meta, // Preserve all original meta data
              section: field?.context?.section || field.meta?.section || null, // Get section from either location
              pageNumber: field.page || field.meta?.pageNumber || 1,
              rect: field.rect || field.meta?.rect || null,
              xPosition: field.meta?.xPosition || null,
              yPosition: field.meta?.yPosition || null,
              width: field.meta?.width || null,
              height: field.meta?.height || null,
            },
          }));
          console.log('Processed form fields:', formData.form_fields);
        }

        // Store the form data
        this.setFormData({ formId, formData });
        this.setCurrentForm(formId);

        // Initialize field values from form data
        const fieldValues = {};
        formData.form_fields.forEach(field => {
          if (field.value !== undefined) {
            fieldValues[field.id] = field.value;
          }
        });
        this.fieldValues = fieldValues;

        // Log the final state
        console.log('Store state after loading form:', {
          currentFormId: this.currentFormId,
          formFields: formData.form_fields.length,
          fieldValues: Object.keys(fieldValues).length,
        });

        return formData;
      } catch (error) {
        console.error('Error loading form data:', error);
        throw error;
      }
    },

    async loadEntities(clientId) {
      console.log('Client ID to load entities', clientId);
      try {
        const response = await fetch(`/api/v1/pdf-forms/entity-fields/?entity_id=${clientId}`);
        if (!response.ok) throw new Error('Failed to fetch entities');

        const data = await response.json();

        console.log('Entities payload:', data);

        // Set all entities
        this.setEntities(data.entities);

        // Set entity fields for all entities
        Object.entries(data.fields_by_entity).forEach(([entityId, fields]) => {
          this.setEntityFields({ entityId, fields });
        });

        return data;
      } catch (error) {
        console.error('Error loading entities:', error);
        Sentry.captureException(error);
        throw error;
      }
    },

    async mapSectionToEntityAPI({ sectionId, entityId }) {
      try {
        // First map the section to the entity
        const response = await fetch('/api/v1/pdf-forms/map-section/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]').value,
          },
          body: JSON.stringify({
            section_id: sectionId,
            entity_id: entityId,
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to map section to entity');
        }

        const data = await response.json();

        // Update the section-entity mapping in the store
        this.sectionEntityMapping[sectionId] = entityId;

        // Now map the fields for this section
        const fields = this.sectionFields(sectionId);
        if (fields.length > 0) {
          // Call the match-fields-to-entities endpoint
          const matchResponse = await fetch('/api/v1/pdf-forms/match-fields-to-entities/', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]').value,
            },
            body: JSON.stringify({
              fields: fields.map(field => ({
                id: field.id,
                label: field.label || field.name,
                value: field.value || '',
                section_id: sectionId,
              })),
              entity_id: entityId,
              entity_fields: this.entityFields,
            }),
          });

          if (!matchResponse.ok) {
            throw new Error('Failed to map fields to entity');
          }

          const matchData = await matchResponse.json();

          // Update the field mappings in the store
          if (matchData.field_mappings) {
            Object.entries(matchData.field_mappings).forEach(([fieldId, mapping]) => {
              if (mapping.entity_field) {
                this.fieldMappings[fieldId] = mapping.entity_field;
                // Also update the field value if one was provided
                if (mapping.value !== undefined) {
                  this.fieldValues[fieldId] = mapping.value;
                }
              }
            });
          }
        }

        return data;
      } catch (error) {
        console.error('Error in mapSectionToEntityAPI:', error);
        throw error;
      }
    },

    async updateFieldValueAPI({ fieldId, value }) {
      try {
        const response = await fetch('/api/v1/pdf-forms/update-field/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]').value,
          },
          body: JSON.stringify({ field_id: fieldId, value }),
        });

        if (!response.ok) throw new Error('Failed to update field value');

        this.setFieldValue({ fieldId, value });

        return await response.json();
      } catch (error) {
        console.error('Error updating field value:', error);
        throw error;
      }
    },

    async linkEntityToSection({ sectionId, entityId, names }) {
      // Add section to processing state
      this.processingSections.push(sectionId);

      try {
        // Get fields for this section
        const sectionFields = this.annotations.filter(field =>
          field.context?.section === sectionId || field.meta?.section === sectionId,
        );

        // Get entity fields - send the raw fields array, not wrapped in an object
        const entityFields = this.entityFields;
        const formid = this.currentFormId;
        // Update the section-entity mapping
        this.sectionEntityMapping[sectionId] = entityId;
        // Force reactivity update with a new object reference
        this.sectionEntityMapping = { ...this.sectionEntityMapping };

        // Call backend to match fields using awaitTask
        const result = await awaitTask('/api/v1/pdf-forms/llm-match-fields/', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': document.querySelector('[name=csrfmiddlewaretoken]').value,
          },
          body: JSON.stringify({
            form_id: formid,
            form_fields: sectionFields,
            entity_fields: entityFields,
            entity_id: entityId,
            section_id: sectionId,
            names: names,
          }),
        });

        if (!result?.task) {
          throw new Error('No task information received from server');
        }

        // If we have field matches in the result, update field values and metadata
        if (result.task?.meta?.matches) {
          const matches = result.task.meta.matches;
          matches.forEach(match => {
            // Update field value and metadata
            this.setFieldValue({
              fieldId: match.id,
              value: match.value || '',
              sourceFields: match.source_fields || [],
              transformation: match.transformation || '',
              confidence: match.confidence || 0,
            });
          });
        }

        // Remove section from processing state
        this.processingSections = this.processingSections.filter(id => id !== sectionId);

        return result;
      } catch (error) {
        // Remove section from processing state on error
        this.processingSections = this.processingSections.filter(id => id !== sectionId);
        throw error;
      }
    },

    async unlinkEntityFromSection(sectionId) {
      // First set to undefined to trigger reactivity
      this.sectionEntityMapping[sectionId] = undefined;
      // Then remove the key
      delete this.sectionEntityMapping[sectionId];
      // Force reactivity update with a new object reference
      this.sectionEntityMapping = { ...this.sectionEntityMapping };

      // Clear any field values associated with this section
      const sectionFields = this.annotations.filter(field =>
        field.context?.section === sectionId || field.meta?.section === sectionId,
      );

      sectionFields.forEach(field => {
        this.setFieldValue({
          fieldId: field.id,
          value: '',
        });
      });

      return true;
    },

    prepareMergeFields(sourceEntityId, targetEntityId) {
      const sourceFields = this.entityFields[sourceEntityId] || [];
      const targetFields = this.entityFields[targetEntityId] || [];
      const fieldGroups = {};

      // Find source and target entities
      const sourceEntity = this.entities.find(e => e.id === sourceEntityId);
      const targetEntity = this.entities.find(e => e.id === targetEntityId);

      if (!sourceEntity || !targetEntity) {
        console.warn('Could not find source or target entity:', { sourceEntityId, targetEntityId });
        return [];
      }

      sourceFields.forEach(field => {
        const key = field.normalized_key;
        if (!fieldGroups[key]) {
          fieldGroups[key] = {
            label: field.original_label || key,
            values: {},
            key,
          };
        }
        fieldGroups[key].values[sourceEntity.name] = field.value;
      });

      targetFields.forEach(field => {
        const key = field.normalized_key;
        if (!fieldGroups[key]) {
          fieldGroups[key] = {
            label: field.original_label || key,
            values: {},
            key,
          };
        }
        fieldGroups[key].values[targetEntity.name] = field.value;
      });

      return Object.values(fieldGroups).map(data => ({
        ...data,
        selectedValue: null,
      }));
    },

    async mergeEntities({ sourceEntityId, targetEntityId, selectedValues }) {
      try {
        // Merge fields
        const sourceFields = this.entityFields[sourceEntityId] || [];
        const targetFields = this.entityFields[targetEntityId] || [];

        // Update target entity fields with selected values
        const mergedFields = [...targetFields];
        Object.entries(selectedValues || {}).forEach(([key, value]) => {
          const existingFieldIndex = mergedFields.findIndex(f => f.normalized_key === key);
          if (existingFieldIndex >= 0) {
            mergedFields[existingFieldIndex] = { ...mergedFields[existingFieldIndex], value };
          } else {
            const sourceField = sourceFields.find(f => f.normalized_key === key);
            if (sourceField) {
              mergedFields.push({ ...sourceField, value });
            }
          }
        });

        // Update sections - remap all sections from source to target
        Object.entries(this.sectionEntityMapping).forEach(([sectionId, entityId]) => {
          if (entityId === sourceEntityId) {
            this.sectionEntityMapping[sectionId] = targetEntityId;
          }
        });

        // Update entity fields
        this.entityFields[targetEntityId] = mergedFields;
        delete this.entityFields[sourceEntityId];

        // Remove source entity from array
        this.entities = this.entities.filter(e => e.id !== sourceEntityId);

        // Force reactivity update for sectionEntityMapping
        this.sectionEntityMapping = { ...this.sectionEntityMapping };

        return true;
      } catch (error) {
        console.error('Error merging entities:', error);
        throw error;
      }
    },

    async pollFieldMatchingTask(taskId, sectionId) {
      try {
        const response = await fetch(`/api/v1/tasks/${taskId}/`);
        if (!response.ok) throw new Error('Failed to fetch task status');

        const task = await response.json();

        if (task.status === 'complete') {
          // Update field values with the matched results
          if (task.result?.matches) {
            task.result.matches.forEach(match => {
              // Update field value and metadata
              this.setFieldValue({
                fieldId: match.id,
                value: match.value || '',
                sourceFields: match.source_fields || [],
                transformation: match.transformation || '',
                confidence: match.confidence || 0,
              });
            });
          }
          // Remove section from processing state
          this.processingSections = this.processingSections.filter(id => id !== sectionId);
        } else if (task.status === 'failed') {
          throw new Error(task.error || 'Field matching failed');
        } else {
          // Continue polling
          setTimeout(() => this.pollFieldMatchingTask(taskId, sectionId), 1000);
        }
      } catch (error) {
        console.error('Error polling task:', error);
        this.processingSections = this.processingSections.filter(id => id !== sectionId);
        throw error;
      }
    },

    setUseDossier(value) {
      this.useDossier = value;
    },

    setUseDocuments(value) {
      this.useDocuments = value;
    },
  },
});
